import React, { useEffect, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { GET_STANDS } from "graphql/query/stand-gql";

import StandGridItem from "./stand-grid-item";
import { Loader, NoData } from "components/request-result";
import { useMe, useVarParam } from "components/use-hooks";
import { Localize, OptimizedGrid } from "components/service";
import { whereConditionBuilder, restorePosition, restoreStandPosition } from "utils";


const StandsGrid = ({ sort, filter }) => {

    const { me } = useMe(),
          location = useLocation(),
          history = useHistory();

    const { state = {}, pathname } = location;
    const hasPagePosition = Object.prototype.hasOwnProperty.call(state, "pagePosition");
    const rafRestoreIdRef = useRef(null);
    const rafPreScrollIdRef = useRef(null);
    const effectiveFetchPolicy = hasPagePosition ? "cache-first" : "cache-and-network";

    const perPage = 30;
    const { data, loading, fetchMore } = useQuery(GET_STANDS, {
        variables: {
            where : whereConditionBuilder( filter.where ),
            whereCrafts : whereConditionBuilder( filter.whereCrafts ),
            orderBy: sort.length ? sort : [{ column: "ID", order : "DESC" }],
            first: perPage,
        },
        fetchPolicy: effectiveFetchPolicy,
        notifyOnNetworkStatusChange: true
    });

    const {
        standsCursor : {
            edges : stands = [],
            pageInfo = {}
        } = {}
    } = data || {};

    const { moment } = useVarParam().get();
    const { exhibitionEvent, eeCurrentId } = me;

    useEffect(() => {
        if (rafPreScrollIdRef.current) {
            window.cancelAnimationFrame(rafPreScrollIdRef.current);
            rafPreScrollIdRef.current = null;
        }

        if (!data || !hasPagePosition) {
            return undefined;
        }
        if (state.returnStandId) {
            if (
                !state.positionChanged &&
                !state.standPositionChanged &&
                typeof state.pagePosition === "number" &&
                state.pagePosition > 0
            ) {
                let attempts = 0;
                const tryPreScroll = () => {
                    attempts += 1;
                    const maxScrollableY = Math.max(
                        0,
                        (document.documentElement?.scrollHeight || 0) - window.innerHeight
                    );
                    const nextY = Math.min(state.pagePosition, maxScrollableY);
                    window.scroll({
                        top: nextY,
                        behavior: "auto"
                    });

                    const canReachTarget = maxScrollableY >= state.pagePosition - 2;
                    const reached = canReachTarget && Math.abs(window.scrollY - state.pagePosition) <= 2;

                    if (reached || attempts >= 240) {
                        history.replace({
                            pathname
                        }, {
                            ...state,
                            positionChanged: true
                        });
                        rafPreScrollIdRef.current = null;
                        return;
                    }

                    rafPreScrollIdRef.current = window.requestAnimationFrame(tryPreScroll);
                };

                rafPreScrollIdRef.current = window.requestAnimationFrame(tryPreScroll);
            }
            return undefined;
        }

        restorePosition(history, pathname, state);
        return () => {
            if (rafPreScrollIdRef.current) {
                window.cancelAnimationFrame(rafPreScrollIdRef.current);
                rafPreScrollIdRef.current = null;
            }
        };
    }, [ data, hasPagePosition, history, pathname, state ]);

    useEffect(() => {
        if (!data) {
            return undefined;
        }
        if (
            state.returnStandId &&
            typeof state.pagePosition === "number" &&
            state.pagePosition > 0 &&
            !state.positionChanged
        ) {
            return undefined;
        }
        return restoreStandPosition({
            history,
            pathname,
            state,
            rafIdRef: rafRestoreIdRef
        });
    }, [ data, history, pathname, state ]);

    return(
        <>
            { !stands.length && !loading &&
                <NoData
                    text={{
                        after :  <Localize br={ <br /> }>NO_DATA.Stands</Localize>
                    }}
                    icon="stand"
                />
            }
            { !!stands.length &&
                <OptimizedGrid
                    listClass = "stand-grid"
                    itemClass = "stand-grid-item-wrap"
                    gridItems = { stands }
                    pageInfo  = { pageInfo }
                    fetchMore = { fetchMore }
                    perPage   = { perPage }
                    model     = "standsCursor"

                >
                    <StandGridItem
                        moment = { moment }
                        me     = { me }
                        uniqueCraftCount = {
                            exhibitionEvent.find(
                                ({ id }) => id === eeCurrentId
                            ).craftCountUniq
                        }
                    />
                </OptimizedGrid>
            }
            { loading && <Loader type="block" /> }
        </>
    );
}

export default StandsGrid;